import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { GraduationCap, RefreshCw, Award, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const m = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  return `${d.getDate()} ${m[d.getMonth()]} ${d.getFullYear()}`;
};

export default function ResultsPage() {
  const { user } = useAuth();
  const isAdult = user && !user.is_child;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [filterChild, setFilterChild] = useState("");
  const [children, setChildren] = useState([]);

  const loadChildren = async () => {
    if (!isAdult) return;
    try {
      const r = await api.get("/users");
      const kids = (r.data?.users || r.data || []).filter((u) => u.is_child);
      setChildren(kids);
    } catch {
      // ignore — fallback: bygg från items
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/vklass/results${filterChild ? `?child_user_id=${filterChild}` : ""}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte hämta resultat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterChild]);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/vklass/results/sync");
      toast.success("Synk startad — kommer ta ~30s. Ladda om snart.");
      setTimeout(() => load(), 35000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte starta synk");
    } finally {
      setSyncing(false);
    }
  };

  // Barn-knappar: prioritera /users-listan (stabil); fallback till items
  const childOptions = (children.length > 0
    ? children.map((c) => [c.id, c.name || "?"])
    : Array.from(new Map(items.map((i) => [i.child_user_id, i.child_name || "?"])).entries())
  );

  return (
    <div className="px-5 pt-3 pb-12" data-testid="results-page">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="label-eyebrow">Skolan</p>
          <h1 className="font-display text-2xl font-medium mt-0.5 flex items-center gap-2">
            <Award size={22} className="text-amber-500" /> Provresultat
          </h1>
        </div>
        {isAdult && (
          <button
            onClick={sync}
            disabled={syncing}
            className="mt-1 px-3 py-2 rounded-full bg-[color:var(--jarvis)] text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="results-sync-btn"
            title="Hämta nya resultat från Vklass"
          >
            <RefreshCw size={12} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Synkar…" : "Synka"}
          </button>
        )}
      </div>

      {isAdult && childOptions.length > 1 && (
        <div className="flex gap-2 flex-wrap mb-4">
          <button
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              filterChild === ""
                ? "bg-[color:var(--jarvis)] text-white border-[color:var(--jarvis)]"
                : "bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)] border-[color:var(--border-subtle)]"
            }`}
            onClick={() => setFilterChild("")}
            data-testid="results-filter-all"
          >
            Alla
          </button>
          {childOptions.map(([id, name]) => (
            <button
              key={id}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                filterChild === id
                  ? "bg-[color:var(--jarvis)] text-white border-[color:var(--jarvis)]"
                  : "bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)] border-[color:var(--border-subtle)]"
              }`}
              onClick={() => setFilterChild(id)}
              data-testid={`results-filter-${name.split(" ")[0].toLowerCase()}`}
            >
              {name.split(" ")[0]}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[color:var(--text-tertiary)]">Hämtar resultat…</p>
      ) : items.length === 0 ? (
        <div className="card-soft p-6 text-center space-y-2">
          <GraduationCap size={28} className="mx-auto text-[color:var(--text-tertiary)]" />
          <p className="text-sm text-[color:var(--text-secondary)]">
            Inga provresultat ännu.
          </p>
          {isAdult && (
            <p className="text-xs text-[color:var(--text-tertiary)]">
              Klicka "Synka från Vklass" för att hämta resultat från Studieöversikten.
              Detta tar ~30 sekunder per barn.
            </p>
          )}
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((r) => {
            const isOpen = !!expanded[r.id];
            const hasFreeText = !!(r.free_text && r.free_text.trim());
            return (
              <li
                key={r.id}
                className="card-soft p-3"
                data-testid={`result-item-${r.id}`}
              >
                <button
                  className="w-full flex items-start justify-between gap-3 text-left"
                  onClick={() => setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.subject && (
                        <span className="text-[10px] uppercase tracking-wide bg-[color:var(--jarvis)]/10 text-[color:var(--jarvis)] px-1.5 py-0.5 rounded">
                          {r.subject}
                        </span>
                      )}
                      {r.category && (
                        <span className="text-[10px] uppercase tracking-wide bg-amber-500/10 text-amber-700 px-1.5 py-0.5 rounded">
                          {r.category}
                        </span>
                      )}
                      {r.status && (
                        <span className="text-[10px] uppercase tracking-wide bg-emerald-500/10 text-emerald-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                          <CheckCircle2 size={10} /> {r.status}
                        </span>
                      )}
                      {isAdult && r.child_name && (
                        <span className="text-[10px] text-[color:var(--text-tertiary)]">
                          {r.child_name.split(" ")[0]}
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-sm mt-1 truncate">{r.title || "Utan titel"}</p>
                    <p className="text-xs text-[color:var(--text-tertiary)]">
                      {fmtDate(r.date)} {r.teacher ? `· ${r.teacher}` : ""}
                    </p>
                  </div>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {isOpen && (
                  <div className="text-xs whitespace-pre-wrap text-[color:var(--text-secondary)] mt-2 pt-2 border-t border-black/5">
                    {hasFreeText ? (
                      <p>{r.free_text}</p>
                    ) : (
                      <p className="text-[color:var(--text-tertiary)] italic">
                        Ingen lärarkommentar
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
